import { useEffect, useState } from "react";
import { ExtendWindow } from "../components/ExtendWindow.js";
import {
  ApiError,
  OBSTACLES,
  REVIEW_DECISIONS,
  type ReviewPrefill,
  type Today,
  api,
  label
} from "../api.js";

/**
 * Кадр 1. Домашний экран показывает следующее решение и следующий шаг, а не счёт.
 * Логирование инлайн: исход одним тапом, препятствие вторым. Код препятствия
 * обязателен — «ничего» такое же значение, как «силы».
 *
 * ⚠️ Разделы собраны в панели, а не в карточку на элемент: тишина и отлёжка — это
 * строки внутри одного стекла. Иначе на экране с двумя протоколами, тишиной и отлёжкой
 * одновременно видно шесть стеклянных слоёв при лимите в четыре.
 */

type Props = { today: Today; onChanged: () => void; onOpenGoal: (id: string) => void };

export function TodayScreen({ today, onChanged, onOpenGoal }: Props): React.JSX.Element {
  return (
    <>
      <header className="head">
        <h1>Сегодня</h1>
        <span className="date">{today.today}</span>
      </header>
      <p className="subhead">
        активных протоколов {today.protocols.running} из {today.protocols.limit}
        {today.forecasts_resolved > 0 && ` · разрешённых прогнозов ${today.forecasts_resolved}`}
      </p>

      {(today.decisions.length > 0 || today.drift.length > 0) && (
        <>
          <div className="sec">Решение</div>
          <section className="card decisions">
            {today.decisions.map((decision) =>
              decision.stage === "outcome" ? (
                <Decision key={decision.protocol_id} decision={decision} onChanged={onChanged} />
              ) : decision.stage === "review" ? (
                <Review key={decision.protocol_id} decision={decision} onChanged={onChanged} />
              ) : (
                <Supersede key={decision.protocol_id} decision={decision} onChanged={onChanged} />
              )
            )}
            {today.drift.map((entry) => (
              <Drift key={entry.protocol_id} drift={entry} onOpenGoal={onOpenGoal} />
            ))}
          </section>
        </>
      )}

      {today.steps.length === 0 && today.decisions.length === 0 && (
        <p className="empty">Идущих протоколов нет. Заведи цель и запусти протокол — тогда здесь появится шаг.</p>
      )}

      {today.steps.length > 0 && (
        <>
          <div className="sec">Следующий шаг</div>
          <section className="card steps">
            {today.steps.map((step) => (
              <StepCard key={step.protocol_id} step={step} onChanged={onChanged} onOpenGoal={onOpenGoal} />
            ))}
          </section>
        </>
      )}

      <Waiting today={today} onOpenGoal={onOpenGoal} onChanged={onChanged} />
    </>
  );
}

/**
 * Всё, что ждёт своего срока или решения, — ОДНОЙ панелью с подписями групп.
 *
 * Тишина, отлёжка и напоминания — три разных повода, но одно состояние: не сегодняшний
 * шаг, а очередь. Раздельными панелями их было пять слоёв в кадре при лимите в четыре,
 * и лимит здесь не формальность: каждый стеклянный слой — отдельный проход композитора.
 * Заголовок раздела вынести наружу нельзя — это и есть второй слой.
 */
function Waiting({
  today,
  onOpenGoal,
  onChanged
}: {
  today: Today;
  onOpenGoal: (id: string) => void;
  onChanged: () => void;
}): React.JSX.Element {
  return (
    <>
      <div className="sec">Ждёт</div>
      <section className="rows">
        {today.silence.length > 0 && (
          <>
            <div className="rows-group">Тишина</div>
            {today.silence.map((entry) => (
              <button className="row" key={entry.goal_id} onClick={() => onOpenGoal(entry.goal_id)}>
                <span className="two-line">
                  {entry.title}
                  <span className="hint">последнее касание {entry.days} дн. назад</span>
                </span>
                <span className="sub">
                  открыть <Chevron />
                </span>
              </button>
            ))}
          </>
        )}

        {today.cooling.length > 0 && (
          <>
            <div className="rows-group">Отлёжка</div>
            {today.cooling.map((goal) => (
              <button className="row" key={goal.id} onClick={() => onOpenGoal(goal.id)}>
                <span className="two-line">
                  {goal.title}
                  <span className="hint">решение {goal.cooling_until?.slice(0, 10) ?? "—"}</span>
                </span>
                <span className="sub">
                  открыть <Chevron />
                </span>
              </button>
            ))}
          </>
        )}

        <div className="rows-group">Напоминания</div>
        <Reminders reminders={today.reminders} onChanged={onChanged} />
      </section>

      {today.silence.length > 0 && (
        <p className="note-under">
          Тишина считает возврат, а не непрерывность: важно, сколько пройдёт до следующего
          касания, а не сколько дней подряд не было пропусков.
        </p>
      )}
      <p className="note-under">
        Напоминание, которое ты завёл сам, приходит сверх суточного бюджета инициативы:
        это твоя просьба, а не непрошеное сообщение. Системное можно снять поимённо —
        чтобы не приходилось выключать бота целиком.
      </p>
    </>
  );
}

/**
 * Исход протокола, окно которого закрылось. Четыре кнопки равного веса — и это
 * не оформление, а механизм 4 против тапалки: `not_executed` отделён от `did_not_work`.
 *
 * «Не сработало» — методика не дала эффекта, это знание о методике.
 * «Не выполнялось» — до методики дело не дошло, это знание о себе.
 * Смешать их значит отравить всё, что система когда-либо узнает о методиках: половина
 * «неработающих» окажется просто невыполненными. Поэтому у кнопок одинаковый вес,
 * ни одна не подсвечена как «правильная», и разница написана прямо под ними.
 */
function Decision({
  decision,
  onChanged
}: {
  decision: Today["decisions"][number];
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { adherence } = decision;

  async function close(outcome: "worked" | "unclear" | "did_not_work" | "not_executed") {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/protocols/${decision.protocol_id}/close`, { outcome });
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не записалось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="decision">
      <div className="what">{decision.hypothesis}</div>
      <p className="goal-line">
        «{decision.goal_title}» · окно {decision.window_start} — {decision.window_end} кончилось
      </p>
      <p className="rule">правило успеха: {decision.success_rule}</p>

      <div className="adherence">
        <div className="bar">
          <i className="done" style={{ width: `${adherence.planned ? (adherence.done / adherence.planned) * 100 : 0}%` }} />
          <i
            className="miss"
            style={{
              width: `${adherence.planned ? Math.max(0, ((adherence.planned - adherence.done) / adherence.planned) * 100) : 0}%`
            }}
          />
        </div>
        <div className="line">
          <span>
            приверженность: <b>{adherence.done} из {adherence.planned}</b> запланированных
          </span>
          <span className="n">n = {adherence.weeks} нед.</span>
        </div>
      </div>

      <div className="outcomes">
        {(
          [
            ["worked", "Сработало"],
            ["unclear", "Неясно"],
            ["did_not_work", "Не сработало"],
            ["not_executed", "Не выполнялось"]
          ] as const
        ).map(([outcome, name]) => (
          <button key={outcome} className="btn" disabled={busy} onClick={() => void close(outcome)}>
            {name}
          </button>
        ))}
      </div>
      <p className="outcome-note">
        «Не сработало» — методика не дала эффекта. «Не выполнялось» — до методики дело не дошло.
        Это разные вещи, и система их не смешивает: иначе половина «неработающих» методик
        окажется просто невыполненными.
      </p>

      {/* Продление — альтернатива исходу: «подводить рано, окно двигаю вперёд». Форма
          общая с карточкой цели, чтобы правило «только вперёд» существовало в одном месте. */}
      <ExtendWindow protocolId={decision.protocol_id} windowEnd={decision.window_end} onChanged={onChanged} />
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/**
 * Преемник: третья ступень. Разбор кончился решением «Меняю» — значит ритм меняется,
 * а менять его у идущего протокола нельзя.
 *
 * ⚠️ Почему не правка на месте. Приверженность считается текущим ритмом по ВСЕМУ окну
 * с начала, поэтому смена ритма на ходу переписала бы прошлое: 4 касания из 9 (44%)
 * стали бы 4 из 6 (67%) без единого нового касания. Приложение похвалило бы за правку
 * плана — ровно то, против чего оно построено. Поэтому предшественник остаётся как есть,
 * а новый ритм живёт в новом эксперименте со ссылкой назад.
 *
 * Метрика и правило успеха наследуются дословно и здесь не спрашиваются: иначе сравнить
 * предшественника с преемником будет не по чему.
 */
function Supersede({
  decision,
  onChanged
}: {
  decision: Today["decisions"][number];
  onChanged: () => void;
}): React.JSX.Element {
  const was = decision.cadence.value ?? 0;
  const [perWeek, setPerWeek] = useState(String(Math.max(1, was - 1)));
  const [hypothesis, setHypothesis] = useState("");
  const [weeks, setWeeks] = useState("4");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const start = new Date();
      const end = new Date(start.getTime() + Number(weeks) * 7 * 86_400_000);
      const iso = (date: Date) => date.toLocaleDateString("sv");
      await api.post(`/api/protocols/${decision.protocol_id}/supersede`, {
        hypothesis: hypothesis.trim(),
        cadence: { kind: "per_week_count", value: Number(perWeek) },
        window_start: iso(start),
        window_end: iso(end)
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не завёлся");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="decision supersede">
      <div className="what">Изменённый протокол</div>
      <p className="goal-line">
        «{decision.goal_title}» · разбор кончился решением «Меняю»
      </p>
      <p className="rule">
        Было {was} раз в неделю, приверженность {decision.adherence.done} из {decision.adherence.planned}
      </p>

      <div className="aar">
        <div className="aar-q">Наследуется без изменений</div>
        <p className="aar-a">{decision.success_rule}</p>
        <p className="aar-from">
          Правило успеха и метрика переносятся дословно — иначе сравнить старый эксперимент
          с новым будет не по чему. Цифры предшественника остаются как есть: смена ритма
          у идущего протокола переписала бы их задним числом.
        </p>
      </div>

      <div className="aar-form">
        <input
          className="field"
          value={hypothesis}
          placeholder="Новая гипотеза: она называет новый ритм"
          onChange={(event) => setHypothesis(event.target.value)}
        />
        <div className="sup-nums">
          <label>
            раз в неделю
            <input
              className="field"
              inputMode="numeric"
              value={perWeek}
              onChange={(event) => setPerWeek(event.target.value)}
            />
          </label>
          <label>
            недель
            <input
              className="field"
              inputMode="numeric"
              value={weeks}
              onChange={(event) => setWeeks(event.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="log-row drift-act">
        <button
          className="btn primary"
          disabled={busy || hypothesis.trim().length < 3 || !Number(perWeek) || !Number(weeks)}
          onClick={() => void create()}
        >
          Завести преемника
        </button>
      </div>
      <p className="outcome-note">
        Предложение снимется само через две недели после разбора — запись решения останется
        в леджере в любом случае. Напоминать вечно об этом нельзя: это было бы то самое
        непрошеное сообщение, от которого продукт защищается бюджетом.
      </p>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/**
 * Разбор протокола: вторая ступень решения (`corpus/methods/after-action-review.md`).
 *
 * Первые два вопроса владелец ЧИТАЕТ, а не пишет: карточка требует брать «что планировали»
 * из записи протокола, а «что произошло» — только из ленты касаний. Через месяц после окна
 * память даёт реконструкцию, и разбор по ней разбирает не то, что было. Его дело — третий
 * и четвёртый вопросы: причины и решение.
 *
 * Решение обязательно. Разбор без решения — дневник, и метрика приёма («доля разборов,
 * где решение отличалось от „продолжаю“») перестаёт что-либо значить.
 *
 * Сверка премортема: код предлагает по кодам препятствий из ленты, владелец подтверждает
 * или переставляет. Последнее слово за ним — «время» в касании и «время» в премортеме
 * могут означать разное, и знает об этом только он.
 */
function Review({
  decision,
  onChanged
}: {
  decision: Today["decisions"][number];
  onChanged: () => void;
}): React.JSX.Element {
  const [prefill, setPrefill] = useState<ReviewPrefill | null>(null);
  const [why, setWhy] = useState("");
  const [next, setNext] = useState("");
  const [marks, setMarks] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<ReviewPrefill>(`/api/protocols/${decision.protocol_id}/review`)
      .then((data) => {
        setPrefill(data);
        // Стартовое положение галочек — предположение кода. Владелец его правит.
        setMarks(Object.fromEntries(data.premortem.map((item) => [item.id, item.suggested])));
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : "разбор не собрался"));
  }, [decision.protocol_id]);

  async function save(choice: string) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/protocols/${decision.protocol_id}/review`, {
        q_why_diff: why.trim() || undefined,
        q_next: next.trim() || undefined,
        decision: choice,
        premortem: marks
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не записалось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="decision review">
      <div className="what">Разбор: «{decision.hypothesis}»</div>
      <p className="goal-line">
        «{decision.goal_title}» · исход: {label(decision.outcome ?? "")}
      </p>

      {!prefill && !error && <p className="pm-empty">собираю из леджера…</p>}

      {prefill && (
        <>
          <div className="aar">
            <div className="aar-q">Что планировали</div>
            <p className="aar-a">{prefill.q_intended}</p>
            <div className="aar-q">Что произошло</div>
            <p className="aar-a num">{prefill.q_happened}</p>
            <p className="aar-from">Оба ответа взяты из леджера, а не из памяти: через месяц память даёт реконструкцию.</p>
          </div>

          {prefill.premortem.length > 0 && (
            <div className="pm-check-list">
              <div className="pm-typical-head">Что ты называл заранее</div>
              {prefill.premortem.map((item) => (
                <button
                  key={item.id}
                  className={`pm-item pm-mark ${marks[item.id] ? "on" : ""}`}
                  onClick={() => setMarks((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                >
                  <span className="pm-text">
                    {item.text}
                    <span className="pm-meta">
                      {label(item.obstacle_code)} ·{" "}
                      {item.seen_in_touches > 0
                        ? `встречалось в ленте: ${item.seen_in_touches}`
                        : "в ленте не встречалось"}
                    </span>
                  </span>
                  <span className="pm-flag">{marks[item.id] ? "сбылась" : "нет"}</span>
                </button>
              ))}
            </div>
          )}

          {prefill.blind_spots.length > 0 && (
            <div className="blind">
              <div className="pm-typical-head">Слепая зона</div>
              <p className="pm-note">
                Случилось, а в премортеме этого не было:{" "}
                {prefill.blind_spots.map((spot) => `${label(spot.obstacle_code)} (${spot.n})`).join(", ")}. Это
                интереснее сбывшихся причин: оно показывает, чего ты не видишь заранее.
              </p>
            </div>
          )}

          <div className="aar-form">
            <input
              className="field"
              value={why}
              placeholder="Почему разошлось? Причины, а не виноватые"
              onChange={(event) => setWhy(event.target.value)}
            />
            <input
              className="field"
              value={next}
              placeholder="Что дальше?"
              onChange={(event) => setNext(event.target.value)}
            />
          </div>

          <div className="outcomes five">
            {REVIEW_DECISIONS.map(([value, name]) => (
              <button key={value} className="btn" disabled={busy} onClick={() => void save(value)}>
                {name}
              </button>
            ))}
          </div>
          <p className="outcome-note">
            Разбор заканчивается решением, иначе это дневник. Решение — не оценка себя:
            «прекращаю» здесь такой же нормальный исход, как «продолжаю», и разбор,
            из которого никогда не выходит «прекращаю», бесполезен.
          </p>
        </>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** «1 касание», «3 касания», «5 касаний» — число рядом с чужим окончанием читается как сбой. */
function touchWord(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 14) return "касаний";
  const ones = n % 10;
  if (ones === 1) return "касание";
  if (ones >= 2 && ones <= 4) return "касания";
  return "касаний";
}

/**
 * Просевшая приверженность у идущего протокола — вопрос к протоколу, а не к себе.
 *
 * Числа считает код тем же порогом, которым пользуется бот: экран и инициатива обязаны
 * видеть один и тот же дрейф, иначе владелец перестаёт верить обоим.
 *
 * ⚠️ **Кнопок «Перенести день» и «Сократить» здесь нет намеренно, хотя в эталоне они
 * нарисованы.** Обе правят идущий протокол, а править предрегистрированный эксперимент
 * на ходу нечем: ревизии заведены у цели (`reviseGoal`), у протокола их нет вовсе.
 * Молча дописать эти кнопки значит отменить смысл слова «предрегистрировано» — протокол
 * либо доигрывается до исхода, либо закрывается с исходом и заменяется новым. Это
 * продуктовое решение владельца, а не то, что дорисовывается ради кнопки на макете.
 */
function Drift({
  drift,
  onOpenGoal
}: {
  drift: Today["drift"][number];
  onOpenGoal: (id: string) => void;
}): React.JSX.Element {
  const { adherence } = drift;
  return (
    <div className="decision drift">
      <div className="what">Приверженность ниже половины</div>
      <p className="goal-line">
        «{drift.hypothesis}» — {adherence.done} {touchWord(adherence.done)} из {adherence.planned} запланированных
      </p>
      <p className="rule num">n = {adherence.weeks} нед. · порог {drift.threshold_pct}%</p>

      <div className="adherence">
        <div className="bar">
          <i className="done" style={{ width: `${adherence.planned ? (adherence.done / adherence.planned) * 100 : 0}%` }} />
          <i
            className="miss"
            style={{
              width: `${adherence.planned ? Math.max(0, ((adherence.planned - adherence.done) / adherence.planned) * 100) : 0}%`
            }}
          />
        </div>
      </div>

      <div className="log-row drift-act">
        <button className="btn" onClick={() => onOpenGoal(drift.goal_id)}>
          Открыть цель
        </button>
      </div>
      <p className="outcome-note">
        Это вопрос к протоколу, а не к себе: расписание, которое не выполняется, — данные
        о расписании. Менять идущий протокол нечем — он доигрывается до исхода либо
        закрывается с исходом и заменяется новым.
      </p>
    </div>
  );
}

/**
 * Напоминания. Своё напоминание владельца идёт СВЕРХ суточного бюджета инициативы —
 * это его собственная просьба, а не непрошеное сообщение (CONCEPT.md, решение 7).
 * До этого экрана попросить было нечем: ручка `POST /api/reminders` существовала
 * с этапа 6 и не вызывалась ниоткуда.
 *
 * Системные напоминания показаны здесь же и их можно снять поимённо. Это клапан:
 * без него надоевший бот уходит в mute целиком, вместе со всем полезным, и заметить
 * момент невозможно.
 */
function Reminders({
  reminders,
  onChanged
}: {
  reminders: Today["reminders"];
  onChanged: () => void;
}): React.JSX.Element {
  const [message, setMessage] = useState("");
  const [at, setAt] = useState(defaultWhen);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (message.trim().length < 3 || !at) return;
    setBusy(true);
    setError(null);
    try {
      // ⚠️ datetime-local отдаёт время БЕЗ пояса. Отправить его как есть значит отдать
      // серверу разбирать строку в СВОЁМ поясе — а сервер живёт в UTC, и «напомни в 9:00»
      // прозвонило бы в 11:00 по Варшаве. `new Date(value)` в браузере разбирает строку
      // в поясе устройства владельца, то есть в том самом, в котором он её и набрал.
      await api.post("/api/reminders", { at: new Date(at).toISOString(), message: message.trim() });
      setMessage("");
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не завелось");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id: string) {
    setError(null);
    try {
      await api.post(`/api/reminders/${id}/cancel`);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не снялось");
    }
  }

  return (
    <>
      {reminders.length === 0 && (
        <div className="row">
          <span className="hint">Ни одного напоминания не ждёт. Ниже можно завести своё.</span>
        </div>
      )}
      {reminders.map((reminder) => (
          <div className="row" key={reminder.id}>
            <span className="two-line">
              {reminder.message}
              <span className="hint">
                {formatWhen(reminder.at)} · {reminder.created_by === "owner" ? "твоё" : "системное"}
              </span>
            </span>
            <button className="linklike sub rem-cancel" onClick={() => void cancel(reminder.id)}>
              снять
            </button>
          </div>
        ))}

      <div className="rem-form">
        <input
          className="field"
          value={message}
          placeholder="Напомнить о чём"
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void create();
          }}
        />
        <input
          className="field"
          type="datetime-local"
          value={at}
          onChange={(event) => setAt(event.target.value)}
          aria-label="когда напомнить"
        />
        <button className="btn primary" disabled={busy || message.trim().length < 3} onClick={() => void create()}>
          Напомнить
        </button>
      </div>
      {error && <p className="error rem-error">{error}</p>}
    </>
  );
}

/** Завтра в 9:00 по устройству владельца — самый частый случай, а не абстрактное «сейчас». */
function defaultWhen(): string {
  const at = new Date();
  at.setDate(at.getDate() + 1);
  at.setHours(9, 0, 0, 0);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

function formatWhen(iso: string): string {
  const at = new Date(iso);
  return at.toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Chevron(): React.JSX.Element {
  return (
    <svg width="8" height="13" viewBox="0 0 9 15" fill="none" stroke="rgba(235,235,245,0.4)" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1.5 1.5L7.5 7.5L1.5 13.5" />
    </svg>
  );
}

function StepCard({
  step,
  onChanged,
  onOpenGoal
}: {
  step: Today["steps"][number];
  onChanged: () => void;
  onOpenGoal: (id: string) => void;
}): React.JSX.Element {
  const [kind, setKind] = useState<"work" | "skipped" | null>(null);
  const [metric, setMetric] = useState("");
  const [effort, setEffort] = useState(3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function log(obstacle: string) {
    if (!kind) return;
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/touches", {
        goal_id: step.goal_id,
        protocol_id: step.protocol_id,
        kind,
        obstacle_code: obstacle,
        effort,
        ...(metric ? { metric_value: Number(metric.replace(",", ".")) } : {})
      });
      setKind(null);
      setMetric("");
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "не записалось");
    } finally {
      setSaving(false);
    }
  }

  const { adherence } = step;

  return (
    <div className="step">
      <p className="what">
        <button className="linklike" onClick={() => onOpenGoal(step.goal_id)}>
          {step.goal_title}
        </button>
      </p>
      <p className="goal-line">
        {step.metric_name}
        {step.metric_unit ? `, ${step.metric_unit}` : ""}
        {step.target_date ? ` · до ${step.target_date}` : ""}
      </p>

      <div className="log-row">
        <button className={`btn ${kind === "work" ? "primary" : ""}`} onClick={() => setKind("work")}>
          Сделал
        </button>
        <button className={`btn ${kind === "skipped" ? "primary" : ""}`} onClick={() => setKind("skipped")}>
          Пропустил
        </button>
      </div>

      {kind && (
        <div className="ask">
          <div className="ask-label">
            Что мешало? <span className="req">обязательно</span>
          </div>
          <div className="chips">
            {OBSTACLES.map(([code, label]) => (
              <button key={code} className="chip" disabled={saving} onClick={() => void log(code)}>
                {label}
              </button>
            ))}
          </div>
          <div className="effort">
            Усилие
            <span className="pips">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  className={`pip ${value <= effort ? "on" : ""}`}
                  onClick={() => setEffort(value)}
                  aria-label={`усилие ${value}`}
                />
              ))}
            </span>
          </div>
          <div className="optional">
            <input
              className="field"
              inputMode="decimal"
              placeholder={step.metric_unit ?? "значение"}
              value={metric}
              onChange={(event) => setMetric(event.target.value)}
            />
            <span className="hint">метрика протокола · можно не заполнять</span>
          </div>
          {error && <p className="error">{error}</p>}
        </div>
      )}

      <div className="adherence">
        <div className="bar">
          <i className="done" style={{ width: `${adherence.planned ? (adherence.done / adherence.planned) * 100 : 0}%` }} />
          <i
            className="miss"
            style={{
              width: `${adherence.planned ? Math.max(0, ((adherence.planned - adherence.done) / adherence.planned) * 100) : 0}%`
            }}
          />
        </div>
        <div className="line">
          <span>
            приверженность: <b>{adherence.done} из {adherence.planned}</b> запланированных
          </span>
          <span className="n">n = {adherence.weeks} нед.</span>
        </div>
      </div>
    </div>
  );
}
